
import { defineComponent, PropType } from "vue";
import LogView from './LogView.vue';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';
import LookupDataService from '@/services/LookupDataService';
import RightToWorkCheckPopup from './RightToWorkCheckPopup.vue';
import { isAdmin, isBoss } from '@/utils/auth';

export default defineComponent({
  name: "JobModal",
  components: {
    LogView,
    Multiselect,
    RightToWorkCheckPopup
  },
  props: {
    baseUrl: { type: String, required: true },
    officers: { type: Array as PropType<any[]>, default: () => [] },
    sites: { type: Array as PropType<any[]>, default: () => [] },
    jobStatuses: { type: Array as PropType<any[]>, default: () => [] },
    officerGroups: { type: Array as PropType<any[]>, default: () => [] },
    defaultGroupIds: { type: Array as PropType<number[]>, default: () => [] },
  },
  emits: ["job-added", "job-updated", "right-to-work-saved"],
  data() {
    return {
      // Officer
      selectedOfficerId: null,
      selectedOfficerName: "",
      selectedJobGroupId: null as number | null,

      isModalVisible: false,
      isEditMode: false,
      isReadOnly: false,
      currentJobId: 0,
      refreshKey: 0,
      
      // Site - keeping original
      siteSearch: '',
      selectedSiteId: 0,
      
      // Other form data
      selectedJobClientId: 0,
      selectedJobStatusId: 0,
      selectedJobDate: "",
      selectedJobStartTime: "0900",
      selectedJobEndTime: "1700",
      selectedJobRemarks: "",
      selectedJobAdjustment: 0,
      selectedJobTravelExpense: 0,
      selectedJobBonusHours: 0,
      selectedJobPayRate: 0,
      selectedSiteClients: [] as any[],
      rateForSelectedClientSite: 0,
      officerCheckWarnings: [] as string[],
      officerCheckOfficerName: "",
      officerCheckRightToWorkWarnings: [] as string[],
      officerCheckSeq: 0,
      showRightToWorkCheck: false,
      // Same roles the RightToWork API allows
      canCheckRightToWork: isAdmin() || isBoss(),

      // Check-call tracking - off by default. selectedJobCheckCallIntervalMinutes
      // is deliberately kept as null|number rather than coerced to 0 like the
      // Financials fields above: 0 would mean "poll continuously", a dangerous
      // silent default if a blank field ever became 0.
      selectedJobRequiresTracking: false,
      selectedJobCheckCallIntervalMinutes: null as number | null
    };
  },
  watch: {
    selectedOfficerId(newId) {
      if (newId) {
        const officer = this.officers.find(o => o.id === newId);
        if (officer) {
          // optional: if you still want the full object somewhere
          this.selectedOfficerName = officer.name;
          // Only auto-fill for new jobs - editing must keep the job's own
          // historically-saved payRate, not the officer's current rate.
          if (!this.isEditMode) {
            this.selectedJobPayRate = officer.payRate || 0;
          }
        }
      }
      this.checkOfficerForJob();
    },
    selectedJobDate() {
      this.checkOfficerForJob();
    },
    selectedJobGroupId(newGroupId) {
      // Changing the group filter should drop a currently-selected officer
      // who no longer matches it, rather than silently keeping a hidden
      // selection around.
      if (!newGroupId || !this.selectedOfficerId) {
        return;
      }
      const officer = this.officers.find((o: any) => o.id === this.selectedOfficerId);
      if (officer && officer.officerGroupId !== newGroupId) {
        this.selectedOfficerId = null;
      }
    }
  },
  computed: {
    // Line that carries the "Check Right to Work" link (-1 = none). Matched against the
    // API's own rightToWorkWarnings list, not by wording.
    lastRightToWorkWarningIndex(): number {
      const rightToWork = this.officerCheckRightToWorkWarnings as string[];
      return (this.officerCheckWarnings as string[]).map(w => rightToWork.includes(w)).lastIndexOf(true);
    },

    // Inactive officers and officers marked non-available for the selected job date are
    // hidden from the dropdown. The officers array already carries active/nonAvailableDates
    // from the cached lookup-data payload (SecurityOfficers/GetAllAsync), so this is a
    // client-side filter only - no extra fetch when the modal opens.
    availableOfficers(): any[] {
      let pool = this.officers.filter((o: any) => {
        // Keep the currently-selected officer visible even if they've since become
        // inactive, so editing an existing job doesn't blank out the selection.
        return o.id === this.selectedOfficerId || o.active !== false;
      });

      // Per-job group override narrows the officer pool further, on top of
      // whatever ScheduleView's own group filter already passed down in `officers`.
      // Strict on purpose (no "keep selected officer" exception here, unlike the
      // active/non-availability filters below): the selectedJobGroupId watcher
      // already clears selectedOfficerId the instant it stops matching this group,
      // so by the time this runs there's nothing stale left to protect.
      if (this.selectedJobGroupId) {
        pool = pool.filter((o: any) => o.officerGroupId === this.selectedJobGroupId);
      }

      if (!this.selectedJobDate) {
        return pool;
      }
      const jobDate = this.selectedJobDate.split('T')[0];
      return pool.filter((o: any) => {
        // Keep the currently-selected officer visible even if they've since become
        // unavailable, so editing an existing job doesn't blank out the selection.
        if (o.id === this.selectedOfficerId) {
          return true;
        }
        // No right to work on this date (expired, or valid-until is before the job date).
        // The backend computes rightToWorkBlockedFrom, so this is just a date comparison.
        const rightToWorkBlockedFrom: string | null = o.rightToWorkBlockedFrom;
        if (rightToWorkBlockedFrom && jobDate >= rightToWorkBlockedFrom.split('T')[0]) {
          return false;
        }
        const nonAvailableDates: string[] = o.nonAvailableDates || [];
        return !nonAvailableDates.some(d => d.split('T')[0] === jobDate);
      });
    },

    // Company-wide fallback interval, already cached client-side as part of
    // lookupData (see AppDataController.GetAllLookupData) - no extra fetch
    // needed just to show it as a placeholder.
    defaultCheckCallIntervalMinutes(): number {
      return LookupDataService.getData()?.defaultCheckCallIntervalMinutes ?? 2;
    }
  },
  methods: {
    // Site - Original handlers
    onSiteSearch(event: any) {
      const found = this.sites.find(s => s.name === event.target.value);
      if (found) {
        this.selectedSiteId = found.id;
        this.onSiteChange();
      } else if (!event.target.value) {
        this.selectedSiteId = 0;
        this.selectedSiteClients = [];
      }
    },

    onSiteChange() {
      this.selectedJobClientId = 0;
      this.rateForSelectedClientSite = 0;
      if (this.selectedSiteId) {
        this.fetchSiteClients(this.selectedSiteId);
      } else {
        this.selectedSiteClients = [];
      }
      this.checkOfficerForJob();
    },

    // Runs Schedule/CheckOfficerForJob for the form's current officer, date, times, site and client
    fetchOfficerCheck(): Promise<any> {
      let jobDate = this.selectedJobDate || this.dateToYYYYMMDD(new Date());
      if (jobDate.includes('T')) {
        jobDate = jobDate.split('T')[0];
      }

      const payload = {
        securityOfficerId: this.selectedOfficerId,
        siteId: this.selectedSiteId || null,
        clientId: this.selectedJobClientId || null,
        jobDate: jobDate,
        startTime: this.toApiTime(this.selectedJobStartTime),
        endTime: this.toApiTime(this.selectedJobEndTime),
        excludeJobId: this.isEditMode ? this.currentJobId : null
      };

      return fetch(`${this.baseUrl}Schedule/CheckOfficerForJob`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      })
      .then((r: Response) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));
    },

    // Site/client bans and overlapping jobs are allowed (customer's call, handovers, short cover) but must be a
    // deliberate choice, so Save/Update re-checks with the final form values rather than trusting the yellow
    // warning band, which may be stale or dismissed.
    async confirmOfficerIssuesIfAny(): Promise<boolean> {
      try {
        const result = await this.fetchOfficerCheck();
        // The API has no ban flag, so pick the ban lines out of Warnings (ScheduleRepository.Format*BanWarning)
        const banWarnings = ((result?.warnings || []) as string[]).filter(w => w.startsWith("Banned for "));
        const sections: string[] = [];
        if (banWarnings.length) {
          sections.push(`BANNED:\n${banWarnings.map(w => `- This officer is ${w.charAt(0).toLowerCase()}${w.slice(1)}.`).join("\n")}`);
        }
        if (result?.hasOverlappingJob) {
          const jobDate = this.selectedJobDate.split('T')[0].split('-').reverse().join('/');
          sections.push(`OVERLAPPING JOB:\n- This officer already has a job overlapping ${this.selectedJobStartTime}-${this.selectedJobEndTime} on ${jobDate}.`);
        }
        if (!sections.length) {
          return true;
        }
        return confirm(`${sections.join("\n\n")}\n\nSave anyway?`);
      } catch (error) {
        console.error("Error checking officer bans and overlapping jobs:", error);
        return confirm("Could not check whether this officer is banned for this site/client or already has an overlapping job.\n\nSave anyway?");
      }
    },

    // Times are checked when the field loses focus, not per keystroke - editing 1200 to 0445 digit by digit
    // passes through several valid times. Partial times can't be overlap-checked, so they're skipped.
    onJobTimeChange() {
      if (this.isValidTime(this.selectedJobStartTime) && this.isValidTime(this.selectedJobEndTime)) {
        this.checkOfficerForJob();
      }
    },

    checkOfficerForJob() {
      // Responses can arrive out of order when fields change quickly, so only the latest check may update the band
      const checkSeq = ++this.officerCheckSeq;

      if (!this.selectedOfficerId) {
        this.officerCheckWarnings = [];
        this.officerCheckOfficerName = "";
        this.officerCheckRightToWorkWarnings = [];
        return;
      }

      this.fetchOfficerCheck()
      .then((result: any) => {
        if (checkSeq !== this.officerCheckSeq) return;
        this.officerCheckWarnings = result?.warnings || [];
        this.officerCheckOfficerName = result?.officerName || this.selectedOfficerName || "";
        this.officerCheckRightToWorkWarnings = result?.rightToWorkWarnings || [];
      })
      .catch((error: Error) => {
        if (checkSeq !== this.officerCheckSeq) return;
        console.error("Error checking officer for job:", error);
        this.officerCheckWarnings = ["Could not check officer eligibility (SIA badge, passport, visa, right to work, bans, availability) due to a connection error. Please retry before saving."];
        this.officerCheckOfficerName = "";
        this.officerCheckRightToWorkWarnings = [];
      });
    },

    // Re-check so the warnings clear straight away, and let the schedule grid refresh its ⚠️ icons
    onRightToWorkSaved() {
      this.checkOfficerForJob();
      this.$emit("right-to-work-saved");
    },

    dateToYYYYMMDD(d: Date | string) {
      if (!d) return "";
      // If it's a string, try to parse it
      const dateObj = typeof d === 'string' ? new Date(d) : d;
      if (isNaN(dateObj.getTime())) return "";
      
      // Get local date components to avoid timezone issues
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    parseDateFromApi(dateString: string) {
      if (!dateString) return "";
      
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
      
      // If it has time component, extract just the date part
      if (dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      
      // Try to parse it as a date
      try {
        const d = new Date(dateString);
        if (!isNaN(d.getTime())) {
          return this.dateToYYYYMMDD(d);
        }
      } catch (e) {
        console.warn('Could not parse date:', dateString);
      }
      
      return dateString;
    },
    
    showModal() {
      this.isModalVisible = true;
      document.body.classList.add("modal-open");
    },
    
    close() {
      this.isModalVisible = false;
      document.body.classList.remove("modal-open");
      this.resetForm();
    },
    
    // prefill is used by the officer jobs calendar (click a day to add a job for that officer and date)
    openForAdd(prefill?: { officerId?: number; jobDate?: string }) {
      this.isEditMode = false;
      this.isReadOnly = false;
      this.currentJobId = 0;
      this.resetForm();
      if (prefill?.officerId) {
        this.selectedOfficerId = prefill.officerId as any;
      }
      if (prefill?.jobDate) {
        this.selectedJobDate = prefill.jobDate;
      }
      this.showModal();
    },

    openForEdit(job: any, readOnly = false) {
      this.isEditMode = true;
      this.isReadOnly = readOnly;
      this.currentJobId = job.jobId;
      this.refreshKey++;
      this.loadJobData(job);
      this.showModal();
    },
    
    resetForm() {
      // Officer
      this.selectedOfficerId = null;
      this.selectedJobGroupId = this.defaultGroupIds.length === 1 ? this.defaultGroupIds[0] : null;

      // Site
      this.selectedSiteId = 0;
      this.siteSearch = '';
      
      // Other
      this.selectedJobClientId = 0;
      this.selectedJobStatusId = 0;
      this.selectedJobDate = this.dateToYYYYMMDD(new Date());
      this.selectedJobStartTime = "0900";
      this.selectedJobEndTime = "1700";
      this.selectedJobRemarks = "";
      this.selectedJobAdjustment = 0;
      this.selectedJobTravelExpense = 0;
      this.selectedJobBonusHours = 0;
      this.selectedJobPayRate = 0;
      this.selectedSiteClients = [];
      this.rateForSelectedClientSite = 0;
      this.officerCheckSeq++;
      this.officerCheckWarnings = [];
      this.officerCheckOfficerName = "";
      this.officerCheckRightToWorkWarnings = [];
      this.showRightToWorkCheck = false;
      this.selectedJobRequiresTracking = false;
      this.selectedJobCheckCallIntervalMinutes = null;
    },

    loadJobData(job: any) {
      // Officer
      this.selectedOfficerId = job.securityOfficerId || null;

      // Group shown/filtered here is the officer's *current* group, not the
      // historical snapshot saved on the job (SecurityOfficer.OfficerGroupId
      // via trg_Job_SnapshotOfficerGroupId) - that snapshot is payroll-only
      // history and is never read back into this picker.
      const assignedOfficer = this.officers.find((o: any) => o.id === this.selectedOfficerId);
      this.selectedJobGroupId = assignedOfficer ? assignedOfficer.officerGroupId ?? null : null;

      // Site
      this.selectedSiteId = job.siteId || 0;
      const site = this.sites.find(s => s.id === this.selectedSiteId);
      this.siteSearch = site ? site.name : '';

      // Other
      this.selectedJobClientId = job.clientId || 0;
      this.selectedJobStatusId = job.statusId || 0;
      
      // FIXED: Use parseDateFromApi method
      this.selectedJobDate = this.parseDateFromApi(job.jobDate);
      
      this.selectedJobStartTime = this.formatTimeString(job.startTime);
      this.selectedJobEndTime = this.formatTimeString(job.endTime);
      this.selectedJobRemarks = job.remarks || "";
      this.selectedJobAdjustment = job.adjustmentHours || 0;
      this.selectedJobTravelExpense = job.travelExpense || 0;
      this.selectedJobBonusHours = job.bonusHours || 0;
      this.selectedJobPayRate = job.payRate || 0;
      this.selectedJobRequiresTracking = !!job.isCheckCallTrackingRequired;
      this.selectedJobCheckCallIntervalMinutes = job.checkCallIntervalMinutes ?? null;

      if (this.selectedSiteId) {
        this.fetchSiteClients(this.selectedSiteId);
      }
    },

    // Times are typed as HHMM (e.g. 1430) - the API still takes and returns HH:MM[:SS]
    formatTimeString(time: any): string {
      if (!time) return "0900";
      if (typeof time === 'string') return time.substring(0, 5).replace(':', '');
      if (typeof time === 'object' && time.Hours !== undefined) {
        return `${time.Hours.toString().padStart(2, '0')}${time.Minutes.toString().padStart(2, '0')}`;
      }
      return "0900";
    },

    isValidTime(time: string): boolean {
      return /^([01][0-9]|2[0-3])[0-5][0-9]$/.test(time);
    },

    // HHMM -> HH:MM, or null while the field is still incomplete/invalid
    toApiTime(time: string): string | null {
      return this.isValidTime(time) ? `${time.substring(0, 2)}:${time.substring(2)}` : null;
    },

    fetchSiteClients(siteId: number) {
      fetch(`${this.baseUrl}AppData/GetClientsForSelectedSite/${siteId}`, {
        headers: { Authorization: `Bearer ${localStorage.token}` }
      })
      .then(r => r.json())
      .then(res => {
        this.selectedSiteClients = res.clients || [];
        if (this.selectedJobClientId) {
          const client = this.selectedSiteClients.find(sc => sc.id == this.selectedJobClientId);
          this.rateForSelectedClientSite = client?.rate || 0;
        }
      })
      .catch(error => console.error("Error fetching site clients:", error));
    },
    
    OnClientChange() {
      this.rateForSelectedClientSite = this.selectedSiteClients.find(sc => sc.id == this.selectedJobClientId)?.rate || 0;
      this.checkOfficerForJob();
    },
    
    OnStatusChange() {
      //console.log("Selected Status ID:", this.selectedJobStatusId);
    },

    // Blank is always valid (falls back to the company default server-side).
    // A non-blank value must be a positive whole number - 0 would mean "poll
    // continuously", which is never an intentional choice here.
    isCheckCallIntervalValid(): boolean {
      const value = this.selectedJobCheckCallIntervalMinutes;
      if (value === null || value === ("" as any)) {
        return true;
      }
      const num = Number(value);
      return Number.isInteger(num) && num > 0;
    },

    async AddJob() {
      if (!this.selectedOfficerId || !this.selectedSiteId || !this.selectedJobClientId || !this.selectedJobStatusId) {
        alert("Please fill all required fields");
        return;
      }

      if (!this.isValidTime(this.selectedJobStartTime) || !this.isValidTime(this.selectedJobEndTime)) {
        alert("Please enter valid time in HHMM format (e.g. 1430)");
        return;
      }

      if (!this.isCheckCallIntervalValid()) {
        alert("Check-Call Interval must be a positive whole number of minutes");
        return;
      }

      if (!(await this.confirmOfficerIssuesIfAny())) {
        return;
      }

      // FIXED: Ensure date is properly formatted
      let jobDate = this.selectedJobDate;
      if (!jobDate) {
        jobDate = this.dateToYYYYMMDD(new Date());
      }

      // FIXED: Ensure date is in YYYY-MM-DD format without timezone issues
      // If the date has a time component, extract just the date part
      if (jobDate.includes('T')) {
        jobDate = jobDate.split('T')[0];
      }

      const newJob = {
        jobId: 0,
        securityOfficerId: this.selectedOfficerId,
        siteId: this.selectedSiteId,
        clientId: this.selectedJobClientId,
        statusId: this.selectedJobStatusId,
        jobDate: jobDate,
        startTime: this.toApiTime(this.selectedJobStartTime) + ":00",
        endTime: this.toApiTime(this.selectedJobEndTime) + ":00",
        payRate: parseFloat(this.selectedJobPayRate as any) || 0,
        rate: this.rateForSelectedClientSite || 0,
        adjustment: parseFloat(this.selectedJobAdjustment as any) || 0,
        travelExpense: parseFloat(this.selectedJobTravelExpense as any) || 0,
        bonusHours: parseFloat(this.selectedJobBonusHours as any) || 0,
        remarks: this.selectedJobRemarks,
        operator: localStorage.getItem("userId"),
        recordDateTime: new Date().toISOString(),
        recordStatusId: 1,
        isCheckCallTrackingRequired: this.selectedJobRequiresTracking,
        checkCallIntervalMinutes: this.selectedJobCheckCallIntervalMinutes === null || this.selectedJobCheckCallIntervalMinutes === ("" as any)
          ? null
          : Number(this.selectedJobCheckCallIntervalMinutes)
      };

      //console.log("Adding job with date:", jobDate);
      //console.log("Full payload:", newJob);

      fetch(`${this.baseUrl}schedule/AddJob`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(newJob)
      })
      .then((r: Response) => {
        if (r.status === 200 || r.status === 204) {
          if (r.status === 204 || r.headers.get('content-length') === '0') {
            return { success: true };
          }
          return r.json();
        } else {
          throw new Error(`HTTP ${r.status}: ${r.statusText}`);
        }
      })
      .then((result: any) => {
        if (result && result.success !== false) {
          alert("Job added successfully!");
          this.close();
          this.$emit("job-added");
        } else {
          alert("Error adding job");
        }
      })
      .catch((error: Error) => {
        console.error("Error adding job:", error);
        alert(`Error adding job: ${error.message}`);
      });
    },

    async UpdateJob() {
      if (!this.selectedOfficerId || !this.selectedSiteId || !this.selectedJobClientId || !this.selectedJobStatusId) {
        alert("Please fill all required fields");
        return;
      }

      if (!this.isValidTime(this.selectedJobStartTime) || !this.isValidTime(this.selectedJobEndTime)) {
        alert("Please enter valid time in HHMM format (e.g. 1430)");
        return;
      }

      if (!this.isCheckCallIntervalValid()) {
        alert("Check-Call Interval must be a positive whole number of minutes");
        return;
      }

      if (!(await this.confirmOfficerIssuesIfAny())) {
        return;
      }

      // FIXED: Ensure date is properly formatted
      let jobDate = this.selectedJobDate;
      if (!jobDate) {
        jobDate = this.dateToYYYYMMDD(new Date());
      }

      // FIXED: Ensure date is in YYYY-MM-DD format without timezone issues
      if (jobDate.includes('T')) {
        jobDate = jobDate.split('T')[0];
      }

      const updatedJob = {
        jobId: this.currentJobId,
        securityOfficerId: this.selectedOfficerId,
        siteId: this.selectedSiteId,
        clientId: this.selectedJobClientId,
        statusId: this.selectedJobStatusId,
        jobDate: jobDate,
        startTime: this.toApiTime(this.selectedJobStartTime) + ":00",
        endTime: this.toApiTime(this.selectedJobEndTime) + ":00",
        payRate: parseFloat(this.selectedJobPayRate as any) || 0,
        rate: this.rateForSelectedClientSite || 0,
        adjustment: parseFloat(this.selectedJobAdjustment as any) || 0,
        travelExpense: parseFloat(this.selectedJobTravelExpense as any) || 0,
        bonusHours: parseFloat(this.selectedJobBonusHours as any) || 0,
        remarks: this.selectedJobRemarks,
        operator: localStorage.getItem("userId"),
        recordDateTime: new Date().toISOString(),
        recordStatusId: 2,
        isCheckCallTrackingRequired: this.selectedJobRequiresTracking,
        checkCallIntervalMinutes: this.selectedJobCheckCallIntervalMinutes === null || this.selectedJobCheckCallIntervalMinutes === ("" as any)
          ? null
          : Number(this.selectedJobCheckCallIntervalMinutes)
      };

      //console.log("Updating job with date:", jobDate);
      //console.log("Full payload:", updatedJob);

      fetch(`${this.baseUrl}schedule/UpdateJob`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedJob)
      })
      .then(async (r: Response) => {
        if (r.status === 200 || r.status === 204) {
          if (r.status === 204 || r.headers.get('content-length') === '0') {
            return { success: true };
          }
          return r.json();
        } else {
          const errorBody = await r.json().catch(() => null);
          const backendMessage = errorBody?.responseResult?.responseMessage || errorBody?.ResponseResult?.ResponseMessage;
          throw new Error(backendMessage || `HTTP ${r.status}: ${r.statusText}`);
        }
      })
      .then((result: any) => {
        if (result && result.success !== false) {
          alert("Job updated successfully!");
          this.refreshKey++;
          this.$emit("job-updated");
          this.close();
        } else {
          alert("Error updating job");
        }
      })
      .catch((error: Error) => {
        console.error("Error updating job:", error);
        alert(`Error updating job: ${error.message}`);
      });
    },
  }
});
